import React, { useState, useEffect, useRef, useCallback } from "react";
import imageCompression from "browser-image-compression";
import Cropper from "react-easy-crop";
import { db, uploadFile, getMyPortfolio, saveMyPortfolio } from "@/api/db";
import { MARKET_PROFESSIONS } from "@/components/market/marketData";
import { PortfolioView } from "./PublicPortfolio";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import {
  UserCircle2, Upload, Plus, Trash2, ExternalLink, Save,
  Check, Loader2, X, ChevronDown, ChevronLeft,
  Sun, Moon, Copy, Eye, GripVertical, Monitor,
  Image, FileText, BarChart3, Briefcase, Code2,
  Wrench, Award, MessageSquareQuote, FolderOpen, Video, Play
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// ─── Quill: register table-paste blot ───────────────────────────────────────
const Quill = ReactQuill.Quill;
const BlockEmbed = Quill.import("blots/block/embed");
const QuillDelta = Quill.import("delta");

class PastedTableBlot extends BlockEmbed {
  static create(value) {
    const node = super.create();
    node.setAttribute("contenteditable", false);
    node.innerHTML = value;
    return node;
  }
  static value(node) {
    return node.innerHTML;
  }
}
PastedTableBlot.blotName = "pastedTable";
PastedTableBlot.tagName = "DIV";
PastedTableBlot.className = "ql-pasted-table";
Quill.register(PastedTableBlot);

// ─── Country codes for WhatsApp selector ─────────────────────────────────────
const COUNTRY_CODES = [
  { code: "+55", country: "Brasil", flag: "🇧🇷" },
  { code: "+1", country: "Estados Unidos", flag: "🇺🇸" },
  { code: "+351", country: "Portugal", flag: "🇵🇹" },
  { code: "+34", country: "Espanha", flag: "🇪🇸" },
  { code: "+44", country: "Reino Unido", flag: "🇬🇧" },
  { code: "+33", country: "França", flag: "🇫🇷" },
  { code: "+49", country: "Alemanha", flag: "🇩🇪" },
  { code: "+39", country: "Itália", flag: "🇮🇹" },
  { code: "+54", country: "Argentina", flag: "🇦🇷" },
  { code: "+56", country: "Chile", flag: "🇨🇱" },
  { code: "+57", country: "Colômbia", flag: "🇨🇴" },
  { code: "+52", country: "México", flag: "🇲🇽" },
  { code: "+81", country: "Japão", flag: "🇯🇵" },
  { code: "+61", country: "Austrália", flag: "🇦🇺" },
  { code: "+41", country: "Suíça", flag: "🇨🇭" },
];

// ─── Profession → Sections mapping ───────────────────────────────────────────

const PROFESSION_SECTIONS = {
  // Visual
  "designer":       ["projects", "tools", "certifications", "testimonials"],
  "web-designer":   ["projects", "tools", "certifications", "testimonials"],
  "videomaker":     ["projects", "tools", "certifications", "testimonials"],
  "editor-videos":  ["projects", "tools", "certifications", "testimonials"],
  "social-media":   ["projects", "metrics", "tools", "certifications"],

  // Texto
  "copywriter":            ["projects", "cases", "tools", "certifications", "testimonials"],
  "redator":               ["projects", "cases", "tools", "certifications", "testimonials"],
  "estrategista-conteudo": ["projects", "metrics", "tools", "certifications"],

  // Metricas
  "gestor-trafego":           ["metrics", "cases", "tools", "certifications"],
  "analista-marketing":       ["metrics", "cases", "tools", "certifications"],
  "analista-email-marketing": ["metrics", "cases", "tools", "certifications"],
  "estrategista-digital":     ["metrics", "cases", "tools", "certifications"],
  "gestor-marketing":         ["metrics", "cases", "tools", "certifications"],
  "analista-infra-mkt":       ["metrics", "tools", "certifications"],

  // Produto/Projetos
  "gestor-produto":             ["cases", "tools", "certifications"],
  "gerente-projetos-digitais":  ["cases", "tools", "certifications"],
  "gestor-operacoes-digitais":  ["cases", "tools", "certifications"],

  // Tech
  "vibe-coder": ["tech_projects", "tools", "certifications"],
};

const DEFAULT_SECTIONS = ["tools", "certifications", "testimonials"];

// ─── Section metadata ────────────────────────────────────────────────────────

const SECTION_META = {
  projects:        { label: "Projetos",             emptyItem: null },
  metrics:         { label: "Resultados",           emptyItem: { value: "", label: "" } },
  cases:           { label: "Cases de Sucesso",     emptyItem: { title: "", desc: "", result: "" } },
  tech_projects:   { label: "Projetos Tech",        emptyItem: null },
  tools:           { label: "Ferramentas",          emptyItem: null },
  certifications:  { label: "Certificações",        emptyItem: { name: "", issuer: "" } },
  testimonials:    { label: "Depoimentos",          emptyItem: { name: "", role: "", company: "", text: "" } },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function slugify(name) {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function isSignedUrl(url) {
  return typeof url === "string" && url.includes("X-Goog-Signature");
}

async function uploadAsset(file, _pathPrefix) {
  const compressed = await imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 2000,
    initialQuality: 0.9,
    useWebWorker: true,
  });
  return uploadFile(compressed, "portfolio-assets");
}

function getCroppedImg(imageSrc, pixelCrop) {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob(blob => {
        if (!blob) { reject(new Error("Canvas toBlob failed")); return; }
        resolve(new File([blob], "cover-cropped.jpg", { type: "image/jpeg" }));
      }, "image/jpeg", 0.92);
    };
    image.onerror = reject;
    image.src = imageSrc;
  });
}

function CoverCropModal({ imageSrc, onSave, onClose }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape" && !saving) onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  async function handleSave() {
    if (!croppedAreaPixels) return;
    setSaving(true);
    try {
      const file = await getCroppedImg(imageSrc, croppedAreaPixels);
      await onSave(file);
      onClose();
    } catch (err) {
      console.error("Crop failed:", err);
    }
    setSaving(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={saving ? undefined : onClose}>
      <div className="w-full max-w-2xl mx-4 rounded-2xl overflow-hidden flex flex-col"
        style={{ background: "#fff", maxHeight: "90vh", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #E5E9EA" }}>
          <h3 className="text-sm font-bold" style={{ color: "#003326", fontFamily: "Poppins, sans-serif" }}>
            Ajustar imagem de capa
          </h3>
          <button onClick={onClose} disabled={saving} className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ color: "#99A6AB", opacity: saving ? 0.4 : 1 }}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Cropper */}
        <div className="relative" style={{ height: "340px", background: "#1a1a2e" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={4}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
        {/* Zoom slider */}
        <div className="flex items-center gap-3 px-6 py-3" style={{ borderTop: "1px solid #E5E9EA" }}>
          <span className="text-xs font-semibold" style={{ color: "#646C6F" }}>Zoom</span>
          <input
            type="range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            onChange={e => setZoom(Number(e.target.value))}
            className="flex-1"
            style={{ accentColor: "#00DCA4" }}
          />
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4" style={{ borderTop: "1px solid #E5E9EA" }}>
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-full text-sm font-semibold"
            style={{ color: "#646C6F", opacity: saving ? 0.4 : 1 }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold"
            style={{ background: "#00DCA4", color: "#003326", opacity: saving ? 0.6 : 1 }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function getSectionsForProfession(professionId) {
  return PROFESSION_SECTIONS[professionId] || DEFAULT_SECTIONS;
}

function parseVideoUrl(url) {
  if (!url) return null;
  let m;
  // YouTube
  m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/);
  if (m) return { provider: "youtube", id: m[1] };
  // Vimeo
  m = url.match(/vimeo\.com\/(\d+)/);
  if (m) return { provider: "vimeo", id: m[1] };
  // Loom
  m = url.match(/loom\.com\/(?:share|embed)\/([\w]+)/);
  if (m) return { provider: "loom", id: m[1] };
  return null;
}

function getVideoThumbnail(parsed) {
  if (!parsed) return null;
  if (parsed.provider === "youtube") return `https://img.youtube.com/vi/${parsed.id}/hqdefault.jpg`;
  if (parsed.provider === "loom") return `https://cdn.loom.com/sessions/thumbnails/${parsed.id}-with-play.gif`;
  return null;
}

function generateBlockId() {
  return Math.random().toString(36).slice(2, 9);
}

function createBlock(type) {
  let data;
  if (type === "projects") data = [];
  else if (type === "tools") data = [];
  else if (type === "tech_projects") data = { github_url: "", projects: [] };
  else if (type === "metrics") data = [{}, {}, {}, {}];
  else data = [];
  return { id: generateBlockId(), type, data };
}

function migrateToBlocks(portfolioData, profession) {
  if (portfolioData?.blocks && Array.isArray(portfolioData.blocks)) {
    // Migrate existing blocks: visual_projects/text_samples → projects
    let projectsBlock = null;
    const migrated = [];
    for (const block of portfolioData.blocks) {
      if (block.type === "visual_projects") {
        const items = (Array.isArray(block.data) ? block.data : []).map(item => ({
          ...item,
          kind: item.type === "gallery" ? "gallery" : "image",
        }));
        if (!projectsBlock) {
          projectsBlock = { id: block.id, type: "projects", data: items };
          migrated.push(projectsBlock);
        } else {
          projectsBlock.data = [...projectsBlock.data, ...items];
        }
      } else if (block.type === "text_samples") {
        const folders = Array.isArray(block.data) ? block.data : [];
        const items = folders.map(f => ({ kind: "copywriting", folder: f.folder || "", files: f.files || [] }));
        if (!projectsBlock) {
          projectsBlock = { id: block.id, type: "projects", data: items };
          migrated.push(projectsBlock);
        } else {
          projectsBlock.data = [...projectsBlock.data, ...items];
        }
      } else {
        migrated.push(block);
      }
    }
    return migrated;
  }
  // Legacy: convert flat object → array of blocks
  const order = getSectionsForProfession(profession);
  const allIds = Object.keys(SECTION_META);
  const blocks = [];
  const seen = new Set();
  let projectsBlock = null;

  const legacyIds = ["visual_projects", "text_samples", ...allIds];
  for (const id of [...order, ...legacyIds]) {
    if (seen.has(id)) continue;
    seen.add(id);
    if (portfolioData && portfolioData[id] != null) {
      if (id === "visual_projects") {
        const items = (Array.isArray(portfolioData[id]) ? portfolioData[id] : []).map(item => ({
          ...item,
          kind: item.type === "gallery" ? "gallery" : "image",
        }));
        if (!projectsBlock) {
          projectsBlock = { id: generateBlockId(), type: "projects", data: items };
          blocks.push(projectsBlock);
        } else {
          projectsBlock.data = [...projectsBlock.data, ...items];
        }
      } else if (id === "text_samples") {
        const folders = Array.isArray(portfolioData[id]) ? portfolioData[id] : [];
        const items = folders.map(f => ({ kind: "copywriting", folder: f.folder || "", files: f.files || [] }));
        if (!projectsBlock) {
          projectsBlock = { id: generateBlockId(), type: "projects", data: items };
          blocks.push(projectsBlock);
        } else {
          projectsBlock.data = [...projectsBlock.data, ...items];
        }
      } else {
        blocks.push({ id: generateBlockId(), type: id, data: portfolioData[id] });
      }
    }
  }
  return blocks;
}

// ─── Small reusable pieces ───────────────────────────────────────────────────

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-semibold" style={{ color: "#646C6F" }}>
        {label}{required && <span style={{ color: "#F5576C" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function Input({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2 rounded-xl text-sm border outline-none transition-colors"
      style={{ borderColor: "#E5E9EA", color: "#2E3233", background: "#fff" }}
      onFocus={e => e.target.style.borderColor = "#00DCA4"}
      onBlur={e => e.target.style.borderColor = "#E5E9EA"}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea
      value={value || ""}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 rounded-xl text-sm border outline-none transition-colors resize-none"
      style={{ borderColor: "#E5E9EA", color: "#2E3233", background: "#fff" }}
      onFocus={e => e.target.style.borderColor = "#00DCA4"}
      onBlur={e => e.target.style.borderColor = "#E5E9EA"}
    />
  );
}

function TagInput({ tags = [], onChange, placeholder }) {
  const [input, setInput] = useState("");
  function add() {
    const val = input.trim();
    if (val && !tags.includes(val)) onChange([...tags, val]);
    setInput("");
  }
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
          placeholder={placeholder || "Digite e pressione Enter"}
          className="flex-1 px-3 py-2 rounded-xl text-sm border outline-none"
          style={{ borderColor: "#E5E9EA", color: "#2E3233" }}
          onFocus={e => e.target.style.borderColor = "#00DCA4"}
          onBlur={e => e.target.style.borderColor = "#E5E9EA"}
        />
        <button onClick={add} className="px-3 py-2 rounded-xl text-sm font-semibold"
          style={{ background: "#EAFAF6", color: "#00664C" }}>
          <Plus className="w-4 h-4" />
        </button>
      </div>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {tags.map(tag => (
            <span key={tag} className="flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ background: "#F2F4F4", color: "#2E3233" }}>
              {tag}
              <button onClick={() => onChange(tags.filter(t => t !== tag))} className="ml-0.5">
                <X className="w-3 h-3" style={{ color: "#99A6AB" }} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ListSection({ items = [], onChange, emptyItem, renderItem, addLabel = "Adicionar" }) {
  function add() { onChange([...items, { ...emptyItem }]); }
  function remove(i) { onChange(items.filter((_, idx) => idx !== i)); }
  function update(i, patch) {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="relative p-4 rounded-xl space-y-3" style={{ background: "#F2F4F4" }}>
          <button onClick={() => remove(i)} className="absolute top-3 right-3"
            style={{ color: "#C8CED0" }}>
            <Trash2 className="w-4 h-4" />
          </button>
          {renderItem(item, i, (patch) => update(i, patch))}
        </div>
      ))}
      <button onClick={add}
        className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors"
        style={{ background: "#EAFAF6", color: "#00664C" }}>
        <Plus className="w-4 h-4" /> {addLabel}
      </button>
    </div>
  );
}

// ─── Section editors ─────────────────────────────────────────────────────────

function VisualProjectsEditor({ data = [], onChange, user }) {
  const [uploading, setUploading] = useState({});

  return (
    <ListSection
      items={data}
      onChange={onChange}
      emptyItem={{ type: "single", images: [], title: "", desc: "" }}
      addLabel="Adicionar projeto"
      renderItem={(item, i, update) => {
        const imgs = item.images || [];
        const isSingle = (item.type || "single") !== "gallery";

        async function handleUpload(e) {
          const files = Array.from(e.target.files || []);
          if (!files.length || !user) return;
          setUploading(prev => ({ ...prev, [i]: true }));
          try {
            const urls = [];
            for (const file of files) {
              const url = await uploadAsset(file, `projects/${user.email}`);
              urls.push(url);
            }
            if (isSingle) {
              update({ images: [urls[0]] });
            } else {
              update({ images: [...imgs, ...urls] });
            }
          } catch (err) {
            console.error("Upload failed:", err);
          }
          setUploading(prev => ({ ...prev, [i]: false }));
          e.target.value = "";
        }

        function removeImage(idx) {
          update({ images: imgs.filter((_, j) => j !== idx) });
        }

        return (
          <>
            <Field label="Tipo">
              <select value={item.type || "single"} onChange={e => update({ type: e.target.value, images: [] })}
                className="w-full px-3 py-2 rounded-xl text-sm border" style={{ borderColor: "#E5E9EA" }}>
                <option value="single">Imagem única</option>
                <option value="gallery">Múltiplas imagens</option>
              </select>
            </Field>

            {imgs.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {imgs.map((url, idx) => (
                  <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden group"
                    style={{ border: "1px solid #E5E9EA" }}>
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(isSingle ? imgs.length === 0 : true) && (
              <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
                style={{ background: "#EAFAF6", color: "#00664C" }}>
                {uploading[i]
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : <Upload className="w-4 h-4" />}
                {isSingle ? "Enviar imagem" : "+ Enviar imagens"}
                <input
                  type="file"
                  accept="image/*"
                  multiple={!isSingle}
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            )}

            <Field label="Título"><Input value={item.title} onChange={v => update({ title: v })} placeholder="Nome do projeto" /></Field>
            <Field label="Descrição"><Textarea value={item.desc} onChange={v => update({ desc: v })} rows={2} /></Field>
          </>
        );
      }}
    />
  );
}

const QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ header: [2, 3, false] }],
    [{ list: "ordered" }, { list: "bullet" }],
  ],
  clipboard: {
    matchers: [
      ["TABLE", function (node) {
        return new QuillDelta().insert({ pastedTable: node.outerHTML });
      }],
    ],
  },
};
const QUILL_FORMATS = ["bold", "italic", "underline", "header", "list", "pastedTable"];

function QuillEditorModal({ title, content, onSave, onClose }) {
  const [value, setValue] = useState(content || "");

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}>
      <div className="w-full max-w-3xl mx-4 rounded-2xl overflow-hidden flex flex-col"
        style={{ background: "#fff", maxHeight: "90vh", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}
        onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #E5E9EA" }}>
          <h3 className="text-sm font-bold" style={{ color: "#003326", fontFamily: "Poppins, sans-serif" }}>
            {title || "Editor de texto"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ color: "#99A6AB" }}>
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* Editor */}
        <div className="flex-1 overflow-y-auto px-6 py-4 quill-modal-editor">
          <ReactQuill
            theme="snow"
            value={value}
            onChange={setValue}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Escreva ou cole seu texto aqui..."
            style={{ minHeight: "300px" }}
          />
          <style>{`
            .quill-modal-editor .ql-container { min-height: 300px; font-size: 14px; }
            .quill-modal-editor .ql-editor { min-height: 280px; }
            .ql-pasted-table { overflow-x: auto; margin: 0.75rem 0; }
            .ql-pasted-table table { width: 100%; border-collapse: collapse; font-size: 14px; }
            .ql-pasted-table td, .ql-pasted-table th { border: 1px solid #E5E9EA; padding: 8px 12px; text-align: left; }
            .ql-pasted-table th { background: #F2F4F4; font-weight: 600; }
            .ql-pasted-table tr:nth-child(even) { background: #FAFBFB; }
          `}</style>
        </div>
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4" style={{ borderTop: "1px solid #E5E9EA" }}>
          <button onClick={onClose}
            className="px-4 py-2 rounded-full text-sm font-semibold"
            style={{ color: "#646C6F" }}>
            Cancelar
          </button>
          <button onClick={() => { onSave(value); onClose(); }}
            className="flex items-center gap-2 px-5 py-2 rounded-full text-sm font-bold"
            style={{ background: "#00DCA4", color: "#003326" }}>
            <Check className="w-4 h-4" /> Salvar
          </button>
        </div>
      </div>
    </div>
  );
}

function CopywritingEditor({ data = [], onChange }) {
  const [editorTarget, setEditorTarget] = useState(null); // { folderIdx, fileIdx }

  function addFile(folderIdx) {
    const next = [...data];
    next[folderIdx] = {
      ...next[folderIdx],
      files: [...(next[folderIdx].files || []), { title: "", content: "" }],
    };
    onChange(next);
  }

  function removeFile(folderIdx, fileIdx) {
    const next = [...data];
    next[folderIdx] = {
      ...next[folderIdx],
      files: next[folderIdx].files.filter((_, j) => j !== fileIdx),
    };
    onChange(next);
  }

  function updateFile(folderIdx, fileIdx, patch) {
    const next = [...data];
    const files = [...next[folderIdx].files];
    files[fileIdx] = { ...files[fileIdx], ...patch };
    next[folderIdx] = { ...next[folderIdx], files };
    onChange(next);
  }

  const editingFile = editorTarget
    ? data[editorTarget.folderIdx]?.files?.[editorTarget.fileIdx]
    : null;

  return (
    <>
      <ListSection
        items={data}
        onChange={onChange}
        emptyItem={{ folder: "", files: [] }}
        addLabel="Adicionar pasta"
        renderItem={(folder, folderIdx, updateFolder) => (
          <>
            <div className="flex items-center gap-2 mb-2">
              <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: "#00664C" }} />
              <Field label="Nome da pasta">
                <Input
                  value={folder.folder}
                  onChange={v => updateFolder({ folder: v })}
                  placeholder="Ex: VSL, Criativos, CPL..."
                />
              </Field>
            </div>

            <div className="space-y-2 ml-2 pl-4" style={{ borderLeft: "2px solid #E5E9EA" }}>
              {(folder.files || []).map((file, fileIdx) => (
                <div key={fileIdx} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
                  <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "#99A6AB" }} />
                  <input
                    value={file.title || ""}
                    onChange={e => updateFile(folderIdx, fileIdx, { title: e.target.value })}
                    placeholder="Título do arquivo"
                    className="flex-1 text-sm outline-none bg-transparent min-w-0"
                    style={{ color: "#2E3233" }}
                  />
                  <button
                    onClick={() => setEditorTarget({ folderIdx, fileIdx })}
                    className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                    style={{ background: "#EAFAF6", color: "#00664C" }}>
                    <FileText className="w-3 h-3" /> Abrir editor
                  </button>
                  <button
                    onClick={() => removeFile(folderIdx, fileIdx)}
                    className="flex-shrink-0"
                    style={{ color: "#C8CED0" }}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>

            <button
              onClick={() => addFile(folderIdx)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold mt-2 ml-6"
              style={{ background: "#fff", color: "#00664C", border: "1px solid #E5E9EA" }}>
              <Plus className="w-3.5 h-3.5" /> Adicionar arquivo
            </button>
          </>
        )}
      />

      {editorTarget && editingFile && (
        <QuillEditorModal
          title={editingFile.title || "Editor de texto"}
          content={editingFile.content}
          onSave={html => updateFile(editorTarget.folderIdx, editorTarget.fileIdx, { content: html })}
          onClose={() => setEditorTarget(null)}
        />
      )}
    </>
  );
}

function ProjectsEditor({ data = [], onChange, user }) {
  const [uploading, setUploading] = useState({});
  const [editorTarget, setEditorTarget] = useState(null); // { itemIdx, fileIdx }

  function addItem(kind) {
    let newItem;
    if (kind === "image") newItem = { kind: "image", images: [], title: "", desc: "" };
    else if (kind === "gallery") newItem = { kind: "gallery", images: [], title: "", desc: "" };
    else if (kind === "video") newItem = { kind: "video", url: "", title: "", desc: "" };
    else newItem = { kind: "copywriting", folder: "", files: [] };
    onChange([...data, newItem]);
    setShowKindPicker(false);
  }

  function removeItem(i) { onChange(data.filter((_, idx) => idx !== i)); }

  function updateItem(i, patch) {
    const next = [...data];
    next[i] = { ...next[i], ...patch };
    onChange(next);
  }

  function addFile(itemIdx) {
    const next = [...data];
    next[itemIdx] = {
      ...next[itemIdx],
      files: [...(next[itemIdx].files || []), { title: "", content: "" }],
    };
    onChange(next);
  }

  function removeFile(itemIdx, fileIdx) {
    const next = [...data];
    next[itemIdx] = {
      ...next[itemIdx],
      files: next[itemIdx].files.filter((_, j) => j !== fileIdx),
    };
    onChange(next);
  }

  function updateFile(itemIdx, fileIdx, patch) {
    const next = [...data];
    const files = [...next[itemIdx].files];
    files[fileIdx] = { ...files[fileIdx], ...patch };
    next[itemIdx] = { ...next[itemIdx], files };
    onChange(next);
  }

  const editingFile = editorTarget
    ? data[editorTarget.itemIdx]?.files?.[editorTarget.fileIdx]
    : null;

  return (
    <>
      <div className="space-y-3">
        {data.map((item, i) => {
          const kind = item.kind || "image";

          if (kind === "copywriting") {
            return (
              <div key={i} className="relative p-4 rounded-xl space-y-3" style={{ background: "#F2F4F4" }}>
                <button onClick={() => removeItem(i)} className="absolute top-3 right-3" style={{ color: "#C8CED0" }}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: "#E0F2FE", color: "#0EA5E9" }}>Copywriting</span>
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-4 h-4 flex-shrink-0" style={{ color: "#00664C" }} />
                  <Field label="Nome da pasta">
                    <Input value={item.folder} onChange={v => updateItem(i, { folder: v })} placeholder="Ex: VSL, Criativos, CPL..." />
                  </Field>
                </div>
                <div className="space-y-2 ml-2 pl-4" style={{ borderLeft: "2px solid #E5E9EA" }}>
                  {(item.files || []).map((file, fileIdx) => (
                    <div key={fileIdx} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
                      <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "#99A6AB" }} />
                      <input
                        value={file.title || ""}
                        onChange={e => updateFile(i, fileIdx, { title: e.target.value })}
                        placeholder="Título do arquivo"
                        className="flex-1 text-sm outline-none bg-transparent min-w-0"
                        style={{ color: "#2E3233" }}
                      />
                      <button
                        onClick={() => setEditorTarget({ itemIdx: i, fileIdx })}
                        className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold flex-shrink-0"
                        style={{ background: "#EAFAF6", color: "#00664C" }}>
                        <FileText className="w-3 h-3" /> Abrir editor
                      </button>
                      <button onClick={() => removeFile(i, fileIdx)} className="flex-shrink-0" style={{ color: "#C8CED0" }}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => addFile(i)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold mt-2 ml-6"
                  style={{ background: "#fff", color: "#00664C", border: "1px solid #E5E9EA" }}>
                  <Plus className="w-3.5 h-3.5" /> Adicionar arquivo
                </button>
              </div>
            );
          }

          if (kind === "video") {
            const parsed = parseVideoUrl(item.url);
            const thumb = getVideoThumbnail(parsed);
            return (
              <div key={i} className="relative p-4 rounded-xl space-y-3" style={{ background: "#F2F4F4" }}>
                <button onClick={() => removeItem(i)} className="absolute top-3 right-3" style={{ color: "#C8CED0" }}>
                  <Trash2 className="w-4 h-4" />
                </button>
                <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: "#FEE2E2", color: "#EF4444" }}>Vídeo</span>
                <Field label="URL do vídeo">
                  <Input value={item.url} onChange={v => updateItem(i, { url: v })} placeholder="Cole o link do YouTube, Vimeo ou Loom" />
                </Field>
                {parsed && (
                  <div className="relative w-32 h-20 rounded-lg overflow-hidden" style={{ background: "#1a1a2e" }}>
                    {thumb ? (
                      <img src={thumb} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1">
                        <Video className="w-5 h-5" style={{ color: "#fff" }} />
                        <span className="text-[10px] font-semibold" style={{ color: "#ccc" }}>{parsed.provider}</span>
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
                        <Play className="w-3.5 h-3.5" style={{ color: "#fff" }} />
                      </div>
                    </div>
                  </div>
                )}
                {!parsed && item.url && (
                  <p className="text-xs" style={{ color: "#EF4444" }}>URL não reconhecida. Use links do YouTube, Vimeo ou Loom.</p>
                )}
                <Field label="Título"><Input value={item.title} onChange={v => updateItem(i, { title: v })} placeholder="Nome do projeto" /></Field>
                <Field label="Descrição"><Textarea value={item.desc} onChange={v => updateItem(i, { desc: v })} rows={2} /></Field>
              </div>
            );
          }

          // image or gallery
          const imgs = item.images || [];
          const isSingle = kind !== "gallery";

          async function handleUpload(e) {
            const files = Array.from(e.target.files || []);
            if (!files.length || !user) return;
            setUploading(prev => ({ ...prev, [i]: true }));
            try {
              const urls = [];
              for (const file of files) {
                const url = await uploadAsset(file, `projects/${user.email}`);
                urls.push(url);
              }
              if (isSingle) {
                updateItem(i, { images: [urls[0]] });
              } else {
                updateItem(i, { images: [...imgs, ...urls] });
              }
            } catch (err) {
              console.error("Upload failed:", err);
            }
            setUploading(prev => ({ ...prev, [i]: false }));
            e.target.value = "";
          }

          function removeImage(idx) {
            updateItem(i, { images: imgs.filter((_, j) => j !== idx) });
          }

          return (
            <div key={i} className="relative p-4 rounded-xl space-y-3" style={{ background: "#F2F4F4" }}>
              <button onClick={() => removeItem(i)} className="absolute top-3 right-3" style={{ color: "#C8CED0" }}>
                <Trash2 className="w-4 h-4" />
              </button>
              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                style={{ background: "#EDE9FE", color: "#8B5CF6" }}>
                {isSingle ? "Imagem única" : "Galeria"}
              </span>

              {imgs.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {imgs.map((url, idx) => (
                    <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden group"
                      style={{ border: "1px solid #E5E9EA" }}>
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeImage(idx)}
                        className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ background: "rgba(0,0,0,0.6)", color: "#fff" }}>
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {(isSingle ? imgs.length === 0 : true) && (
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer transition-colors"
                  style={{ background: "#EAFAF6", color: "#00664C" }}>
                  {uploading[i]
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Upload className="w-4 h-4" />}
                  {isSingle ? "Enviar imagem" : "+ Enviar imagens"}
                  <input type="file" accept="image/*" multiple={!isSingle} onChange={handleUpload} className="hidden" />
                </label>
              )}

              <Field label="Título"><Input value={item.title} onChange={v => updateItem(i, { title: v })} placeholder="Nome do projeto" /></Field>
              <Field label="Descrição"><Textarea value={item.desc} onChange={v => updateItem(i, { desc: v })} rows={2} /></Field>
            </div>
          );
        })}

        {/* Add project buttons */}
        <div className="space-y-2">
          <p className="text-xs font-semibold" style={{ color: "#99A6AB" }}>Adicionar projeto</p>
          <div className="flex flex-wrap gap-2">
            {[
              { kind: "image", label: "Imagem única", icon: Image, color: "#8B5CF6", bg: "#EDE9FE" },
              { kind: "gallery", label: "Galeria", icon: Image, color: "#8B5CF6", bg: "#EDE9FE" },
              { kind: "video", label: "Vídeo", icon: Video, color: "#EF4444", bg: "#FEE2E2" },
              { kind: "copywriting", label: "Copywriting", icon: FileText, color: "#0EA5E9", bg: "#E0F2FE" },
            ].map(opt => {
              const IconComp = opt.icon;
              return (
                <button
                  key={opt.kind}
                  onClick={() => addItem(opt.kind)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-colors"
                  style={{ background: opt.bg, color: opt.color, border: `1px solid ${opt.bg}` }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = opt.color; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = opt.bg; }}>
                  <IconComp className="w-3.5 h-3.5" />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {editorTarget && editingFile && (
        <QuillEditorModal
          title={editingFile.title || "Editor de texto"}
          content={editingFile.content}
          onSave={html => updateFile(editorTarget.itemIdx, editorTarget.fileIdx, { content: html })}
          onClose={() => setEditorTarget(null)}
        />
      )}
    </>
  );
}

function MetricsEditor({ data = [{},{},{},{}], onChange }) {
  const metrics = data.length >= 4 ? data.slice(0, 4) : [...data, ...Array(4 - data.length).fill({})];
  return (
    <div className="grid grid-cols-2 gap-3">
      {metrics.map((m, i) => (
        <div key={i} className="p-3 rounded-xl space-y-2" style={{ background: "#F2F4F4" }}>
          <Input value={m.value} onChange={v => {
            const next = [...metrics];
            next[i] = { ...next[i], value: v };
            onChange(next);
          }} placeholder="Ex: 3x ROAS" />
          <Input value={m.label} onChange={v => {
            const next = [...metrics];
            next[i] = { ...next[i], label: v };
            onChange(next);
          }} placeholder="Ex: Retorno medio" />
        </div>
      ))}
    </div>
  );
}

function CasesEditor({ data = [], onChange }) {
  return (
    <ListSection
      items={data}
      onChange={onChange}
      emptyItem={{ title: "", desc: "", result: "" }}
      addLabel="Adicionar case"
      renderItem={(item, i, update) => (
        <>
          <Field label="Título"><Input value={item.title} onChange={v => update({ title: v })} placeholder="Nome do case" /></Field>
          <Field label="Descrição"><Textarea value={item.desc} onChange={v => update({ desc: v })} rows={2} /></Field>
          <Field label="Resultado"><Input value={item.result} onChange={v => update({ result: v })} placeholder="Ex: +250% em conversões" /></Field>
        </>
      )}
    />
  );
}

function TechProjectsEditor({ data, onChange }) {
  const d = data && typeof data === "object" ? data : { github_url: "", projects: [] };
  const set = (key, val) => onChange({ ...d, [key]: val });
  return (
    <div className="space-y-4">
      <Field label="URL do GitHub">
        <Input value={d.github_url} onChange={v => set("github_url", v)} placeholder="https://github.com/..." />
      </Field>
      <ListSection
        items={d.projects || []}
        onChange={v => set("projects", v)}
        emptyItem={{ name: "", desc: "", url: "", stack: [] }}
        addLabel="Adicionar projeto"
        renderItem={(item, i, update) => (
          <>
            <Field label="Nome"><Input value={item.name} onChange={v => update({ name: v })} placeholder="Nome do projeto" /></Field>
            <Field label="Descrição"><Textarea value={item.desc} onChange={v => update({ desc: v })} rows={2} /></Field>
            <Field label="URL"><Input value={item.url} onChange={v => update({ url: v })} placeholder="https://github.com/..." /></Field>
            <Field label="Tecnologias">
              <TagInput tags={item.stack || []} onChange={v => update({ stack: v })} placeholder="Ex: React, Node.js..." />
            </Field>
          </>
        )}
      />
    </div>
  );
}

function CertificationsEditor({ data = [], onChange }) {
  return (
    <ListSection
      items={data}
      onChange={onChange}
      emptyItem={{ name: "", issuer: "" }}
      addLabel="Adicionar certificação"
      renderItem={(item, i, update) => (
        <>
          <Field label="Certificação"><Input value={item.name} onChange={v => update({ name: v })} placeholder="Nome do certificado" /></Field>
          <Field label="Emissor"><Input value={item.issuer} onChange={v => update({ issuer: v })} placeholder="Ex: Google, Meta, HubSpot" /></Field>
        </>
      )}
    />
  );
}

function TestimonialsEditor({ data = [], onChange }) {
  return (
    <ListSection
      items={data}
      onChange={onChange}
      emptyItem={{ name: "", role: "", company: "", text: "" }}
      addLabel="Adicionar depoimento"
      renderItem={(item, i, update) => (
        <>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Nome"><Input value={item.name} onChange={v => update({ name: v })} placeholder="Nome" /></Field>
            <Field label="Cargo"><Input value={item.role} onChange={v => update({ role: v })} placeholder="Cargo" /></Field>
          </div>
          <Field label="Empresa"><Input value={item.company} onChange={v => update({ company: v })} placeholder="Empresa" /></Field>
          <Field label="Depoimento"><Textarea value={item.text} onChange={v => update({ text: v })} rows={3} /></Field>
        </>
      )}
    />
  );
}

function SectionEditor({ sectionId, data, onChange, user }) {
  switch (sectionId) {
    case "projects":        return <ProjectsEditor data={data} onChange={onChange} user={user} />;
    case "metrics":         return <MetricsEditor data={data} onChange={onChange} />;
    case "cases":           return <CasesEditor data={data} onChange={onChange} />;
    case "tech_projects":   return <TechProjectsEditor data={data} onChange={onChange} />;
    case "tools":           return <TagInput tags={data || []} onChange={onChange} placeholder="Ex: Figma, Google Ads, Notion..." />;
    case "certifications":  return <CertificationsEditor data={data} onChange={onChange} />;
    case "testimonials":    return <TestimonialsEditor data={data} onChange={onChange} />;
    default: return null;
  }
}

// ─── Profile section (collapsible) ──────────────────────────────────────────

function ProfileStep({ form, setField, user, avatarRef, coverRef, handleAvatarUpload, handleCoverUpload, uploadingAvatar, uploadingCover }) {
  return (
    <div className="space-y-6">
      {/* Cover upload */}
      <div
        className="relative h-32 rounded-xl overflow-hidden flex items-center justify-center cursor-pointer"
        style={{ background: form.cover_url ? "transparent" : "#F2F4F4", border: "2px dashed #E5E9EA" }}
        onClick={() => coverRef.current?.click()}>
        {form.cover_url
          ? <img src={form.cover_url} alt="Capa" className="w-full h-full object-cover" />
          : (
            <div className="flex flex-col items-center gap-1">
              {uploadingCover
                ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#99A6AB" }} />
                : <Upload className="w-5 h-5" style={{ color: "#99A6AB" }} />}
              <span className="text-xs" style={{ color: "#99A6AB" }}>Clique para enviar capa</span>
            </div>
          )}
        {form.cover_url && (
          <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
            <Upload className="w-5 h-5 text-white" />
          </div>
        )}
      </div>
      <input ref={coverRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />

      {/* Avatar upload */}
      <div className="flex items-center gap-3">
        <div
          className="relative w-20 h-20 rounded-full overflow-hidden cursor-pointer flex-shrink-0"
          style={{ background: "#F2F4F4", border: "3px solid #fff", boxShadow: "0 0 0 2px #E5E9EA" }}
          onClick={() => avatarRef.current?.click()}>
          {form.avatar_url
            ? <img src={form.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
            : (
              <div className="w-full h-full flex items-center justify-center">
                {uploadingAvatar
                  ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#99A6AB" }} />
                  : <UserCircle2 className="w-10 h-10" style={{ color: "#C8CED0" }} />}
              </div>
            )}
        </div>
        <div>
          <p className="text-sm font-semibold" style={{ color: "#2E3233" }}>Foto de perfil</p>
          <button onClick={() => avatarRef.current?.click()}
            className="text-xs font-semibold mt-1" style={{ color: "#00664C" }}>
            {uploadingAvatar ? "Enviando..." : "Alterar foto"}
          </button>
        </div>
      </div>
      <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />

      {/* Form fields */}
      <Field label="Nome completo" required>
        <Input value={form.full_name} onChange={v => setField("full_name", v)} placeholder="Seu nome completo" />
      </Field>

      <Field label="Headline">
        <Input value={form.headline} onChange={v => setField("headline", v)} placeholder="Ex: Gestor de Trafego | Especialista em Meta Ads" />
      </Field>

      <Field label="Profissão" required>
        <select
          value={form.profession || ""}
          onChange={e => setField("profession", e.target.value)}
          className="w-full px-3 py-2 rounded-xl text-sm border outline-none"
          style={{ borderColor: "#E5E9EA", color: form.profession ? "#2E3233" : "#99A6AB", background: "#fff" }}>
          <option value="">Selecione sua profissão</option>
          {MARKET_PROFESSIONS.map(p => (
            <option key={p.id} value={p.id}>{p.emoji} {p.name}</option>
          ))}
        </select>
      </Field>

      <Field label="Bio">
        <Textarea value={form.bio} onChange={v => setField("bio", v)} rows={4}
          placeholder="Fale um pouco sobre você, sua trajetória e o que te motiva..." />
      </Field>

      <Field label="Localização">
        <Input value={form.location} onChange={v => setField("location", v)} placeholder="Ex: São Paulo, SP" />
      </Field>

      <div className="pt-2 border-t" style={{ borderColor: "#E5E9EA" }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>Contato</p>
        <div className="space-y-3">
          <Field label="WhatsApp">
            <div className="flex gap-2">
              <Select value={form.phoneCountryCode || "+55"} onValueChange={v => setField("phoneCountryCode", v)}>
                <SelectTrigger className="w-[100px] shrink-0 rounded-xl text-sm" style={{ borderColor: "#E5E9EA", background: "#fff" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.flag} {c.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={form.phoneNumber} onChange={v => setField("phoneNumber", v.replace(/\D/g, ""))} placeholder="11999999999" />
            </div>
          </Field>
          <Field label="E-mail de contato">
            <Input value={form.email_contact} onChange={v => setField("email_contact", v)} placeholder="seu@email.com" type="email" />
          </Field>
        </div>
      </div>

      {/* Color mode */}
      <div className="pt-2 border-t" style={{ borderColor: "#E5E9EA" }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>Personalização de cores</p>

        {/* Light / Dark toggle */}
        <div className="flex gap-3 mb-4">
          {[
            { value: "white", label: "Claro",  icon: <Sun className="w-4 h-4" />, bg: "#fff",    border: "#E5E9EA" },
            { value: "dark",  label: "Escuro", icon: <Moon className="w-4 h-4" />, bg: "#161B22", border: "#30363D" },
          ].map(opt => {
            const active = (form.color_mode || "white") === opt.value;
            return (
              <button key={opt.value}
                onClick={() => {
                  if (opt.value === "dark" && (!form.accent_color || form.accent_color === "#00DCA4")) {
                    setField("color_mode", opt.value);
                    setField("accent_color", "#ffffff");
                  } else if (opt.value === "white" && form.accent_color === "#ffffff") {
                    setField("color_mode", opt.value);
                    setField("accent_color", "#00DCA4");
                  } else {
                    setField("color_mode", opt.value);
                  }
                }}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all"
                style={{
                  border: `2px solid ${active ? "#00DCA4" : opt.border}`,
                  background: active ? "#EAFAF6" : opt.bg,
                  color: active ? "#003326" : (opt.value === "dark" ? "#8B949E" : "#646C6F"),
                  cursor: "pointer",
                }}>
                {opt.icon}
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Color pickers */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.accent_color || "#00DCA4"}
              onChange={e => setField("accent_color", e.target.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0"
              style={{ background: "none" }}
            />
            <div>
              <p className="text-xs font-semibold" style={{ color: "#2E3233" }}>Cor principal</p>
              <p className="text-[10px]" style={{ color: "#99A6AB" }}>Botoes, destaques, icones</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.gradient_start || "#00DCA4"}
              onChange={e => setField("gradient_start", e.target.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0"
              style={{ background: "none" }}
            />
            <div>
              <p className="text-xs font-semibold" style={{ color: "#2E3233" }}>Gradiente inicio</p>
              <p className="text-[10px]" style={{ color: "#99A6AB" }}>Cor inicial do header</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={form.gradient_end || "#00664C"}
              onChange={e => setField("gradient_end", e.target.value)}
              className="w-9 h-9 rounded-lg cursor-pointer border-0 p-0"
              style={{ background: "none" }}
            />
            <div>
              <p className="text-xs font-semibold" style={{ color: "#2E3233" }}>Gradiente fim</p>
              <p className="text-[10px]" style={{ color: "#99A6AB" }}>Cor final do header</p>
            </div>
          </div>
        </div>
      </div>

      {/* Layout columns */}
      <div className="pt-2 border-t" style={{ borderColor: "#E5E9EA" }}>
        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: "#99A6AB" }}>Layout</p>
        <div className="flex gap-2">
          {[
            { value: 2, label: "2 colunas" },
            { value: 3, label: "3 colunas" },
            { value: 4, label: "4 colunas" },
          ].map(opt => {
            const active = (form.columns || 2) === opt.value;
            return (
              <button key={opt.value}
                onClick={() => setField("columns", opt.value)}
                className="flex-1 flex flex-col items-center gap-1.5 py-3 rounded-xl text-xs font-semibold transition-all"
                style={{
                  border: `2px solid ${active ? "#00DCA4" : "#E5E9EA"}`,
                  background: active ? "#EAFAF6" : "#fff",
                  color: active ? "#003326" : "#646C6F",
                  cursor: "pointer",
                }}>
                <div className="flex gap-0.5">
                  {Array.from({ length: opt.value }).map((_, i) => (
                    <div key={i} className="rounded-sm" style={{
                      width: `${24 / opt.value}px`,
                      height: "16px",
                      background: active ? "#00DCA4" : "#C8CED0",
                    }} />
                  ))}
                </div>
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Visible rows */}
        <p className="text-xs font-bold uppercase tracking-wider mb-3 mt-4" style={{ color: "#99A6AB" }}>Linhas visíveis</p>
        <div className="flex gap-2">
          {[
            { value: 0, label: "Sem limite" },
            { value: 1, label: "1 linha" },
            { value: 2, label: "2 linhas" },
            { value: 3, label: "3 linhas" },
          ].map(opt => {
            const active = (form.max_rows || 0) === opt.value;
            return (
              <button key={opt.value}
                onClick={() => setField("max_rows", opt.value)}
                className="flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all text-center"
                style={{
                  border: `2px solid ${active ? "#00DCA4" : "#E5E9EA"}`,
                  background: active ? "#EAFAF6" : "#fff",
                  color: active ? "#003326" : "#646C6F",
                  cursor: "pointer",
                }}>
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Editor Toolbar (sticky) ─────────────────────────────────────────────────

function EditorToolbar({ form, setField, saving, handleSave, copied, setCopied }) {
  function previewUrl() {
    const isProd = window.location.hostname !== "localhost";
    const base = isProd ? "https://portustalent.pro" : window.location.origin;
    return `${base}/${form?.slug || ""}`;
  }

  function handleCopy() {
    navigator.clipboard.writeText(previewUrl()).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex-shrink-0 z-20 px-5 py-3 flex items-center gap-3"
      style={{ background: "#fff", borderBottom: "1px solid #E5E9EA", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
      {/* Left: Back */}
      <a href="/"
        className="flex items-center gap-1.5 text-sm font-semibold transition-colors mr-2"
        style={{ color: "#646C6F", textDecoration: "none" }}
        onMouseEnter={e => e.currentTarget.style.color = "#2E3233"}
        onMouseLeave={e => e.currentTarget.style.color = "#646C6F"}>
        <ChevronLeft className="w-4 h-4" />
        Voltar
      </a>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Save */}
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-all"
          style={{
            background: saving ? "#B0FCEA" : "#00DCA4",
            color: "#003326",
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.8 : 1,
          }}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Salvando..." : "Salvar"}
        </button>

        {/* Publish toggle */}
        <button
          onClick={() => setField("is_published", !form.is_published)}
          className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all"
          style={{
            background: form.is_published ? "#EAFAF6" : "#F2F4F4",
            color: form.is_published ? "#00664C" : "#646C6F",
            border: `1px solid ${form.is_published ? "#00DCA4" : "#E5E9EA"}`,
          }}>
          <div style={{
            width: 32, height: 18, borderRadius: 99, position: "relative",
            background: form.is_published ? "#00DCA4" : "#C8CED0",
            flexShrink: 0, transition: "background 0.15s",
          }}>
            <div style={{
              position: "absolute", top: 2, width: 14, height: 14, borderRadius: "50%",
              background: "#fff", transition: "left 0.15s",
              left: form.is_published ? 16 : 2,
            }} />
          </div>
          {form.is_published ? "Publicado" : "Rascunho"}
        </button>

        {/* Copy link */}
        <button onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold"
          style={{ background: "#F2F4F4", color: "#646C6F" }}>
          {copied ? <Check className="w-3.5 h-3.5" style={{ color: "#00664C" }} /> : <Copy className="w-3.5 h-3.5" />}
          {copied ? "Copiado!" : "Link"}
        </button>

        {/* Preview link */}
        {form.slug && (
          <a href={previewUrl()} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-semibold"
            style={{ background: "#F2F4F4", color: "#646C6F", textDecoration: "none" }}>
            <ExternalLink className="w-3.5 h-3.5" />
            Ver
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Collapsible Profile Section ─────────────────────────────────────────────

function CollapsibleProfileSection({ expanded, onToggle, children }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4"
        style={{ background: "#fff", border: "none", cursor: "pointer" }}>
        <div className="flex items-center gap-3">
          <UserCircle2 className="w-5 h-5" style={{ color: "#00DCA4" }} />
          <span className="text-sm font-bold" style={{ color: "#2E3233" }}>Perfil</span>
        </div>
        <ChevronDown className="w-4 h-4" style={{
          color: "#C8CED0",
          transform: expanded ? "rotate(180deg)" : "none",
          transition: "transform 0.15s",
        }} />
      </button>
      {expanded && (
        <div className="px-5 pb-5">
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Block Card ──────────────────────────────────────────────────────────────

function BlockCard({ block, index, collapsed, onToggle, onDelete, onDataChange, dragHandleProps, user }) {
  const meta = SECTION_META[block.type];
  if (!meta) return null;

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "#fff", border: "1px solid #E5E9EA" }}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: collapsed ? "none" : "1px solid #E5E9EA" }}>
        {/* Drag handle */}
        <div {...dragHandleProps} className="cursor-grab active:cursor-grabbing p-1 rounded"
          style={{ color: "#C8CED0", touchAction: "none" }}>
          <GripVertical className="w-4 h-4" />
        </div>

        {/* Label */}
        <span className="text-sm font-bold flex-1" style={{ color: "#2E3233" }}>{meta.label}</span>

        {/* Collapse toggle */}
        <button onClick={onToggle} className="p-1 rounded" style={{ color: "#C8CED0" }}>
          <ChevronDown className="w-4 h-4" style={{
            transform: collapsed ? "none" : "rotate(180deg)",
            transition: "transform 0.15s",
          }} />
        </button>

        {/* Delete */}
        <button onClick={onDelete} className="p-1 rounded hover:bg-red-50 transition-colors"
          style={{ color: "#C8CED0" }}>
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="px-4 py-4">
          <SectionEditor
            sectionId={block.type}
            data={block.data}
            onChange={onDataChange}
            user={user}
          />
        </div>
      )}
    </div>
  );
}

// ─── Block Type Picker ───────────────────────────────────────────────────────

const BLOCK_ICONS = {
  projects:        { icon: Image,              color: "#8B5CF6", bg: "#EDE9FE" },
  metrics:         { icon: BarChart3,           color: "#F59E0B", bg: "#FEF3C7" },
  cases:           { icon: Briefcase,           color: "#10B981", bg: "#D1FAE5" },
  tech_projects:   { icon: Code2,              color: "#6366F1", bg: "#E0E7FF" },
  tools:           { icon: Wrench,             color: "#F97316", bg: "#FFEDD5" },
  certifications:  { icon: Award,              color: "#EAB308", bg: "#FEF9C3" },
  testimonials:    { icon: MessageSquareQuote,  color: "#EC4899", bg: "#FCE7F3" },
};

function BlockTypePicker({ existingTypes, onAdd, onClose }) {
  const allTypes = Object.keys(SECTION_META);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg mx-4 rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid #E5E9EA" }}>
          <div>
            <span className="text-base font-bold" style={{ color: "#2E3233" }}>Adicionar bloco</span>
            <p className="text-xs mt-0.5" style={{ color: "#99A6AB" }}>Escolha uma seção para adicionar ao seu portfólio</p>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 transition-colors" style={{ color: "#C8CED0" }}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 grid grid-cols-2 gap-3">
          {allTypes.map(type => {
            const meta = SECTION_META[type];
            const iconInfo = BLOCK_ICONS[type] || { icon: Plus, color: "#00DCA4", bg: "#EAFAF6" };
            const IconComp = iconInfo.icon;
            const alreadyAdded = existingTypes.has(type);
            return (
              <button
                key={type}
                disabled={alreadyAdded}
                onClick={() => { onAdd(type); onClose(); }}
                className="flex flex-col items-center gap-2.5 p-4 rounded-xl text-center transition-all"
                style={{
                  background: alreadyAdded ? "#F9FAFB" : "#fff",
                  border: alreadyAdded ? "1px solid #E5E9EA" : "1px solid #E5E9EA",
                  cursor: alreadyAdded ? "not-allowed" : "pointer",
                  opacity: alreadyAdded ? 0.5 : 1,
                }}
                onMouseEnter={e => { if (!alreadyAdded) { e.currentTarget.style.borderColor = iconInfo.color; e.currentTarget.style.background = iconInfo.bg + "33"; } }}
                onMouseLeave={e => { if (!alreadyAdded) { e.currentTarget.style.borderColor = "#E5E9EA"; e.currentTarget.style.background = "#fff"; } }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ background: alreadyAdded ? "#F2F4F4" : iconInfo.bg }}>
                  <IconComp className="w-5 h-5" style={{ color: alreadyAdded ? "#C8CED0" : iconInfo.color }} />
                </div>
                <span className="text-xs font-semibold" style={{ color: alreadyAdded ? "#C8CED0" : "#2E3233" }}>
                  {meta.label}
                </span>
                {alreadyAdded && (
                  <span className="text-[10px] font-medium" style={{ color: "#C8CED0" }}>Adicionado</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Block List (DnD) ────────────────────────────────────────────────────────

function BlockList({ blocks, setBlocks, collapsedBlocks, setCollapsedBlocks, user }) {
  function onDragEnd(result) {
    if (!result.destination) return;
    const reordered = Array.from(blocks);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setBlocks(reordered);
  }

  function toggleCollapse(blockId) {
    setCollapsedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }

  function deleteBlock(blockId) {
    setBlocks(prev => prev.filter(b => b.id !== blockId));
  }

  function updateBlockData(blockId, newData) {
    setBlocks(prev => prev.map(b => b.id === blockId ? { ...b, data: newData } : b));
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="blocks">
        {(provided) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-3">
            {blocks.map((block, index) => (
              <Draggable key={block.id} draggableId={block.id} index={index}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    style={{
                      ...provided.draggableProps.style,
                      opacity: snapshot.isDragging ? 0.85 : 1,
                    }}>
                    <BlockCard
                      block={block}
                      index={index}
                      collapsed={collapsedBlocks.has(block.id)}
                      onToggle={() => toggleCollapse(block.id)}
                      onDelete={() => deleteBlock(block.id)}
                      onDataChange={(newData) => updateBlockData(block.id, newData)}
                      dragHandleProps={provided.dragHandleProps}
                      user={user}
                    />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function MyPortfolio() {
  const [user, setUser] = useState(null);
  const [form, setForm] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [collapsedBlocks, setCollapsedBlocks] = useState(() => {
    try {
      const saved = localStorage.getItem("portus_collapsed_blocks");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch { return new Set(); }
  });
  useEffect(() => {
    try { localStorage.setItem("portus_collapsed_blocks", JSON.stringify([...collapsedBlocks])); } catch {}
  }, [collapsedBlocks]);
  const [profileExpanded, setProfileExpanded] = useState(true);
  const [showBlockPicker, setShowBlockPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverCropSrc, setCoverCropSrc] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saveToast, setSaveToast] = useState(null);
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(null);
  const avatarRef = useRef();
  const coverRef = useRef();

  useEffect(() => { init(); }, []);

  async function init() {
    setLoading(true);
    const minDelay = new Promise(r => setTimeout(r, 3000));
    try {
      const u = await db.auth.me();
      setUser(u);
      if (!u) { await minDelay; setLoading(false); return; }

      const existing = await getMyPortfolio();

      // Fetch profile avatar as fallback
      let profileAvatarUrl = "";
      try {
        const prof = await db.profile.get();
        if (prof?.avatarUrl) profileAvatarUrl = prof.avatarUrl;
      } catch {}

      if (existing) {
        const pd = existing.portfolio_data || {};
        // Parse whatsapp into country code + number
        let phoneCountryCode = "+55";
        let phoneNumber = existing.whatsapp || "";
        if (phoneNumber) {
          const match = COUNTRY_CODES.find(c => phoneNumber.startsWith(c.code));
          if (match) {
            phoneCountryCode = match.code;
            phoneNumber = phoneNumber.slice(match.code.length);
          }
        }
        setForm({
          ...existing,
          avatar_url: (existing.avatar_url && !isSignedUrl(existing.avatar_url)) ? existing.avatar_url : profileAvatarUrl,
          color_mode: pd.color_mode || existing.color_mode || "white",
          accent_color: pd.accent_color || "",
          gradient_start: pd.gradient_start || "",
          gradient_end: pd.gradient_end || "",
          columns: pd.columns || 2,
          max_rows: pd.max_rows || 0,
          phoneCountryCode,
          phoneNumber,
        });
        const migratedBlocks = migrateToBlocks(pd, existing.profession);
        setBlocks(migratedBlocks);
      } else {
        const profiles = await db.entities.UserProfile.filter({ user_email: u.email }, "-created_at", 1);
        const vocResult = profiles[0]?.vocational_result || "";
        const matchedProf = MARKET_PROFESSIONS.find(p => p.name === vocResult);
        const professionId = matchedProf?.id || "";
        const slug = slugify(u.full_name || u.email.split("@")[0]);

        setForm({
          user_email: u.email,
          slug,
          profession: professionId,
          template: "default",
          color_mode: "white",
          is_published: false,
          full_name: u.full_name || "",
          headline: "",
          bio: "",
          avatar_url: profileAvatarUrl,
          cover_url: "",
          location: "",
          email_contact: u.email,
          whatsapp: "",
          phoneCountryCode: "+55",
          phoneNumber: "",
          linkedin_url: "",
        });

        // Create suggested blocks for the profession
        const sections = getSectionsForProfession(professionId);
        setBlocks(sections.map(s => createBlock(s)));
      }
    } catch (err) {
      console.error(err);
    }
    await minDelay;
    setLoading(false);
  }

  function setField(key, val) {
    setForm(f => ({ ...f, [key]: val }));

    // When profession changes, suggest adding new blocks
    if (key === "profession" && val) {
      const newSections = getSectionsForProfession(val);
      const existingTypes = new Set(blocks.map(b => b.type));
      const missingTypes = newSections.filter(s => !existingTypes.has(s));

      if (missingTypes.length > 0) {
        const profName = MARKET_PROFESSIONS.find(p => p.id === val)?.name || val;
        setShowSuggestionDialog({ profName, missingTypes });
      }
    }
  }

  function handleAcceptSuggestions() {
    if (!showSuggestionDialog) return;
    const newBlocks = showSuggestionDialog.missingTypes.map(s => createBlock(s));
    setBlocks(prev => [...prev, ...newBlocks]);
    setShowSuggestionDialog(null);
  }

  function handleDismissSuggestions() {
    setShowSuggestionDialog(null);
  }

  function addBlock(type) {
    setBlocks(prev => [...prev, createBlock(type)]);
  }

  async function handleAvatarUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const url = await uploadAsset(file, `avatars/${user.email}`);
      setField("avatar_url", url);
    } catch (err) { console.error(err); }
    setUploadingAvatar(false);
  }

  function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCoverCropSrc(reader.result);
    reader.readAsDataURL(file);
    e.target.value = "";
  }

  async function handleCoverCropSave(croppedFile) {
    setUploadingCover(true);
    try {
      const url = await uploadAsset(croppedFile, `covers/${user.email}`);
      setField("cover_url", url);
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setUploadingCover(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        user_email: user.email,
        slug: form.slug,
        profession: form.profession,
        template: form.template || "default",
        is_published: form.is_published,
        full_name: form.full_name,
        headline: form.headline,
        bio: form.bio,
        avatar_url: isSignedUrl(form.avatar_url) ? "" : form.avatar_url,
        cover_url: form.cover_url,
        location: form.location,
        email_contact: form.email_contact,
        whatsapp: form.phoneNumber ? (form.phoneCountryCode || "+55") + form.phoneNumber : "",
        portfolio_data: {
          blocks,
          color_mode: form.color_mode || "white",
          accent_color: form.accent_color || "",
          gradient_start: form.gradient_start || "",
          gradient_end: form.gradient_end || "",
          columns: form.columns || 2,
          max_rows: form.max_rows || 0,
        },
        updated_at: new Date().toISOString(),
      };
      await saveMyPortfolio(payload);
      setSaveToast({ type: "success", message: "Portfólio salvo com sucesso!" });
    } catch (err) {
      console.error(err);
      setSaveToast({ type: "error", message: "Erro ao salvar portfólio. Tente novamente." });
    }
    setSaving(false);
    setTimeout(() => setSaveToast(null), 3000);
  }

  // ─── Render: loading / no user ─────────────────────────────────────────────

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center" style={{ background: "#F2F4F4" }}>

        {/* ── Orbital stage: face centered, decorations orbit via SVG ── */}
        <div style={{ width: 200, height: 200, position: "relative" }}>
          {/* Orbital ring with gears + sparkles — single SVG, perfectly centered */}
          <svg width="200" height="200" viewBox="0 0 200 200" fill="none"
            style={{ position: "absolute", inset: 0, opacity: 0, animation: "uhuFadeIn 0.4s 0.6s ease-out both" }}>

            {/* Orbit ring — subtle dashed circle */}
            <circle cx="100" cy="100" r="88" stroke="#E5E9EA" strokeWidth="1" strokeDasharray="4 6" opacity="0.6" />

            {/* Gear 1 — top-right, orbits clockwise */}
            <g style={{ transformOrigin: "100px 100px", animation: "uhuOrbit 8s linear infinite" }}>
              <g transform="translate(170, 60)">
                <g style={{ transformOrigin: "0 0", animation: "uhuGearSpin 3s linear infinite" }}>
                  <circle r="7" fill="none" stroke="#C8CED0" strokeWidth="1.5" />
                  <circle r="3" fill="none" stroke="#C8CED0" strokeWidth="1" />
                  {[0,45,90,135,180,225,270,315].map(deg => (
                    <line key={deg} x1="0" y1="-6" x2="0" y2="-10" stroke="#C8CED0" strokeWidth="1.5" strokeLinecap="round"
                      transform={`rotate(${deg})`} />
                  ))}
                </g>
              </g>
            </g>

            {/* Gear 2 — bottom-left, orbits counter-clockwise */}
            <g style={{ transformOrigin: "100px 100px", animation: "uhuOrbitReverse 10s linear infinite" }}>
              <g transform="translate(24, 148)">
                <g style={{ transformOrigin: "0 0", animation: "uhuGearSpinReverse 4s linear infinite" }}>
                  <circle r="9" fill="none" stroke="#D5DADC" strokeWidth="1.5" />
                  <circle r="4" fill="none" stroke="#D5DADC" strokeWidth="1" />
                  {[0,40,80,120,160,200,240,280,320].map(deg => (
                    <line key={deg} x1="0" y1="-8" x2="0" y2="-13" stroke="#D5DADC" strokeWidth="1.5" strokeLinecap="round"
                      transform={`rotate(${deg})`} />
                  ))}
                </g>
              </g>
            </g>

            {/* Gear 3 — left, orbits clockwise slower */}
            <g style={{ transformOrigin: "100px 100px", animation: "uhuOrbit 12s 0.5s linear infinite" }}>
              <g transform="translate(16, 72)">
                <g style={{ transformOrigin: "0 0", animation: "uhuGearSpin 5s linear infinite" }}>
                  <circle r="6" fill="none" stroke="#E0E4E5" strokeWidth="1.2" />
                  <circle r="2.5" fill="none" stroke="#E0E4E5" strokeWidth="0.8" />
                  {[0,60,120,180,240,300].map(deg => (
                    <line key={deg} x1="0" y1="-5" x2="0" y2="-8.5" stroke="#E0E4E5" strokeWidth="1.2" strokeLinecap="round"
                      transform={`rotate(${deg})`} />
                  ))}
                </g>
              </g>
            </g>

            {/* Sparkles — 4-point stars at fixed orbital positions */}
            {[
              { cx: 100, cy: 6, size: 5, delay: "0.8s", dur: "2s" },
              { cx: 188, cy: 130, size: 4, delay: "1.3s", dur: "2.2s" },
              { cx: 56, cy: 186, size: 4.5, delay: "1.7s", dur: "1.8s" },
              { cx: 6, cy: 100, size: 3.5, delay: "2.1s", dur: "2.4s" },
              { cx: 168, cy: 168, size: 3, delay: "1.0s", dur: "2.1s" },
              { cx: 36, cy: 28, size: 3.5, delay: "1.5s", dur: "1.9s" },
            ].map((s, i) => (
              <g key={i} style={{ opacity: 0, animation: `uhuSparkle ${s.dur} ${s.delay} ease-in-out infinite` }}>
                <path d={`M${s.cx},${s.cy - s.size} L${s.cx + s.size * 0.35},${s.cy} L${s.cx},${s.cy + s.size} L${s.cx - s.size * 0.35},${s.cy} Z`}
                  fill="#00DCA4" opacity="0.5" />
                <path d={`M${s.cx - s.size},${s.cy} L${s.cx},${s.cy - s.size * 0.35} L${s.cx + s.size},${s.cy} L${s.cx},${s.cy + s.size * 0.35} Z`}
                  fill="#00DCA4" opacity="0.5" />
              </g>
            ))}
          </svg>

          {/* ühu face — perfectly centered via flexbox */}
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div className="relative" style={{
              width: 96, height: 96,
              background: "#fff",
              borderRadius: "50%",
              boxShadow: "0 4px 24px rgba(0,51,38,0.10)",
              opacity: 0,
              animation: "uhuPop 0.5s 0.2s cubic-bezier(0.34,1.56,0.64,1) both",
            }}>
              <svg width="96" height="96" viewBox="0 0 96 96" fill="none" style={{ display: "block" }}>
                {/* Left eye */}
                <rect x="25" y="24" width="14" height="14" rx="5" fill="#00DCA4"
                  style={{ opacity: 0, animation: "uhuDotDrop 0.35s 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards" }} />
                {/* Right eye */}
                <rect x="57" y="24" width="14" height="14" rx="5" fill="#00DCA4"
                  style={{ opacity: 0, animation: "uhuDotDrop 0.35s 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards" }} />
                {/* U smile */}
                <path d="M24 52 C24 74, 72 74, 72 52" stroke="#00DCA4" strokeWidth="5.5" strokeLinecap="round" fill="none"
                  style={{ strokeDasharray: 78, strokeDashoffset: 78, animation: "uhuDraw 0.5s 0.9s ease-out forwards" }} />
              </svg>
              {/* Blink overlay */}
              <svg width="96" height="96" viewBox="0 0 96 96" fill="none"
                className="absolute inset-0" style={{ opacity: 0, animation: "uhuBlink 3.5s 2.5s ease-in-out infinite" }}>
                <rect width="96" height="96" rx="48" fill="#fff" />
                <line x1="25" y1="31" x2="39" y2="31" stroke="#00DCA4" strokeWidth="3.5" strokeLinecap="round" />
                <line x1="57" y1="31" x2="71" y2="31" stroke="#00DCA4" strokeWidth="3.5" strokeLinecap="round" />
                <path d="M24 52 C24 74, 72 74, 72 52" stroke="#00DCA4" strokeWidth="5.5" strokeLinecap="round" fill="none" />
              </svg>
            </div>
          </div>
        </div>

        {/* Wordmark + progress */}
        <div className="mt-6 flex flex-col items-center">
          <div style={{ opacity: 0, animation: "uhuFadeUp 0.4s 0.8s ease-out both" }}>
            <span className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Poppins, sans-serif", color: "#9BA5A8" }}>
              port
            </span>
            <span className="text-xl font-extrabold tracking-tight" style={{ fontFamily: "Poppins, sans-serif", color: "#00DCA4" }}>
              üs
            </span>
          </div>
          <div className="w-40 h-1 rounded-full overflow-hidden mt-4 mb-3" style={{ background: "#E5E9EA", opacity: 0, animation: "uhuFadeIn 0.3s 1.0s ease-out both" }}>
            <div className="h-full rounded-full" style={{
              width: 0,
              background: "linear-gradient(90deg, #00DCA4, #0EA5E9)",
              animation: "uhuProgress 2.5s 1.0s ease-in-out forwards",
            }} />
          </div>
          <p className="text-xs font-medium" style={{ color: "#99A6AB", opacity: 0, animation: "uhuFadeUp 0.3s 1.2s ease-out both" }}>
            Montando seu portfólio...
          </p>
        </div>

        <style>{`
          @keyframes uhuFadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes uhuPop {
            0% { opacity: 0; transform: scale(0); }
            100% { opacity: 1; transform: scale(1); }
          }
          @keyframes uhuDotDrop {
            0% { opacity: 0; transform: translateY(-8px) scale(0.5); }
            100% { opacity: 1; transform: translateY(0) scale(1); }
          }
          @keyframes uhuDraw {
            to { stroke-dashoffset: 0; }
          }
          @keyframes uhuBlink {
            0%, 88%, 100% { opacity: 0; }
            92%, 96% { opacity: 1; }
          }
          @keyframes uhuFadeUp {
            0% { opacity: 0; transform: translateY(8px); }
            100% { opacity: 1; transform: translateY(0); }
          }
          @keyframes uhuProgress {
            0% { width: 0%; }
            100% { width: 100%; }
          }
          @keyframes uhuOrbit {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes uhuOrbitReverse {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
          }
          @keyframes uhuGearSpin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes uhuGearSpinReverse {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(-360deg); }
          }
          @keyframes uhuSparkle {
            0%, 100% { opacity: 0; transform: scale(0.5); }
            50% { opacity: 0.7; transform: scale(1.1); }
          }
        `}</style>
      </div>
    );
  }

  if (!user || !form) {
    return (
      <div className="text-center py-24">
        <p className="font-semibold" style={{ color: "#2E3233" }}>Você precisa estar logado para acessar esta página.</p>
      </div>
    );
  }

  const existingTypes = new Set(blocks.map(b => b.type));

  // Build live preview object
  const previewPortfolio = {
    ...form,
    whatsapp: form.phoneNumber ? (form.phoneCountryCode || "+55") + form.phoneNumber : "",
    portfolio_data: {
      blocks,
      color_mode: form.color_mode || "white",
      accent_color: form.accent_color || "",
      gradient_start: form.gradient_start || "",
      gradient_end: form.gradient_end || "",
      columns: form.columns || 2,
      max_rows: form.max_rows || 0,
    },
  };

  // ─── Main render ──────────────────────────────────────────────────────────

  return (
    <>
      {/* Mobile gate: shown on screens < 1024px */}
      <div className="flex lg:hidden items-center justify-center min-h-screen px-6" style={{ background: "#F2F4F4" }}>
        <div className="text-center space-y-6 max-w-sm">
          <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: "#EAFAF6" }}>
            <Monitor className="w-8 h-8" style={{ color: "#00664C" }} />
          </div>
          <div className="space-y-2">
            <h2 className="text-lg font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              Editor de Portfólio
            </h2>
            <p className="text-sm" style={{ color: "#646C6F" }}>
              Para a melhor experiência, acesse o editor de portfólio pelo computador.
            </p>
          </div>
          <a href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all"
            style={{ background: "#00DCA4", color: "#003326", textDecoration: "none" }}>
            <ChevronLeft className="w-4 h-4" />
            Voltar para a plataforma
          </a>
        </div>
      </div>

      {/* Desktop: split-screen editor */}
      <div className="hidden lg:flex flex-col h-screen" style={{ background: "#F2F4F4" }}>
        {/* Toolbar (full width, sticky top) */}
        <EditorToolbar
          form={form}
          setField={setField}
          saving={saving}
          handleSave={handleSave}
          copied={copied}
          setCopied={setCopied}
        />

        {/* Body: sidebar + preview */}
        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar (editor) */}
          <style>{`
            .portus-sidebar::-webkit-scrollbar { width: 6px; }
            .portus-sidebar::-webkit-scrollbar-track { background: transparent; }
            .portus-sidebar::-webkit-scrollbar-thumb { background: #C4CBCD; border-radius: 999px; }
            .portus-sidebar::-webkit-scrollbar-thumb:hover { background: #99A6AB; }
            .portus-sidebar { scrollbar-width: thin; scrollbar-color: #C4CBCD transparent; }
          `}</style>
          <div className="w-[420px] flex-shrink-0 overflow-y-auto border-r portus-sidebar" style={{ borderColor: "#E5E9EA", background: "#F2F4F4" }}>
            <div className="p-4 space-y-4">
              {/* Collapsible Profile */}
              <CollapsibleProfileSection
                expanded={profileExpanded}
                onToggle={() => setProfileExpanded(prev => !prev)}>
                <ProfileStep
                  form={form}
                  setField={setField}
                  user={user}
                  avatarRef={avatarRef}
                  coverRef={coverRef}
                  handleAvatarUpload={handleAvatarUpload}
                  handleCoverUpload={handleCoverUpload}
                  uploadingAvatar={uploadingAvatar}
                  uploadingCover={uploadingCover}
                />
              </CollapsibleProfileSection>

              {/* Blocks (DnD) */}
              <BlockList
                blocks={blocks}
                setBlocks={setBlocks}
                collapsedBlocks={collapsedBlocks}
                setCollapsedBlocks={setCollapsedBlocks}
                user={user}
              />

              {/* Add block button */}
              <div className="flex justify-center pt-2 pb-4">
                <button
                  onClick={() => setShowBlockPicker(true)}
                  className="flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold transition-all"
                  style={{
                    background: "#fff",
                    color: "#00664C",
                    border: "2px dashed #00DCA4",
                    cursor: "pointer",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#EAFAF6"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}>
                  <Plus className="w-5 h-5" />
                  Adicionar bloco
                </button>
              </div>
            </div>
          </div>

          {/* Preview (live) */}
          <div className="flex-1 overflow-y-auto" style={{ background: "#E5E9EA" }}>
            <div className="min-h-full">
              <PortfolioView portfolio={previewPortfolio} hideCta />
            </div>
          </div>
        </div>
      </div>

      {/* Block type picker modal */}
      {showBlockPicker && (
        <BlockTypePicker
          existingTypes={existingTypes}
          onAdd={addBlock}
          onClose={() => setShowBlockPicker(false)}
        />
      )}

      {/* Profession suggestion dialog */}
      {showSuggestionDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.4)" }}>
          <div className="w-full max-w-sm mx-4 rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
            <div className="p-6 space-y-4">
              <p className="text-sm font-bold" style={{ color: "#2E3233" }}>
                Adicionar blocos sugeridos para {showSuggestionDialog.profName}?
              </p>
              <div className="flex flex-wrap gap-2">
                {showSuggestionDialog.missingTypes.map(type => (
                  <span key={type} className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ background: "#EAFAF6", color: "#00664C" }}>
                    {SECTION_META[type]?.label || type}
                  </span>
                ))}
              </div>
              <div className="flex gap-3">
                <button onClick={handleDismissSuggestions}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-semibold"
                  style={{ background: "#F2F4F4", color: "#646C6F" }}>
                  Nao, obrigado
                </button>
                <button onClick={handleAcceptSuggestions}
                  className="flex-1 px-4 py-2.5 rounded-full text-sm font-bold"
                  style={{ background: "#00DCA4", color: "#003326" }}>
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cover crop modal */}
      {coverCropSrc && (
        <CoverCropModal
          imageSrc={coverCropSrc}
          onSave={handleCoverCropSave}
          onClose={() => setCoverCropSrc(null)}
        />
      )}

      {/* Save toast notification */}
      {saveToast && (
        <div
          className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold shadow-lg"
          style={{
            background: saveToast.type === "success" ? "#00DCA4" : "#F5576C",
            color: saveToast.type === "success" ? "#003326" : "#fff",
          }}>
          {saveToast.type === "success"
            ? <Check className="w-4 h-4" />
            : <X className="w-4 h-4" />}
          {saveToast.message}
        </div>
      )}
    </>
  );
}
